import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus, Check, X } from "lucide-react";

// Sesli + titreşimli geri bildirim — kullanıcı telefona bakmadan "açıldı/eklendi" anlar
const beep = (freq = 1200, dur = 160, vol = 0.25) => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur / 1000 + 0.02);
    osc.onended = () => { try { ctx.close(); } catch {} };
  } catch {}
};
const vib = (p) => { try { navigator.vibrate?.(p); } catch {} };

/**
 * QUICK ADD DIALOG — Stok sayımında aynı barkod tekrar okutulduğunda açılır.
 * Kullanım senaryosu:
 *   "Şarap ilk sayımda 5 adet okutuldu. Depodan 5 adet daha geldi.
 *    Barkodu tekrar okut → bu dialog açılır → 5 yaz → ✓ Ekle →
 *    yeni toplam otomatik 10 olur."
 */
export default function QuickAddDialog({ data, onClose, onAdd }) {
  const open = !!data;
  const isKg = data && (data.unit === "kg" || data.sell_by === "kg");
  const unitLabel = isKg ? "kg" : "ad";
  const defaultQty = isKg ? 0.5 : 1;
  const quickPicks = isKg ? [0.1, 0.25, 0.5, 1, 2, 5] : [1, 5, 10, 12, 24, 50];
  const [qty, setQty] = useState(defaultQty);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQty(defaultQty);
      // 🔊 Dikkat çeken ikili bip + uzun titreşim — kullanıcı telefona bakmadan anlasın
      beep(900, 120, 0.3);
      setTimeout(() => beep(1400, 180, 0.3), 150);
      vib([80, 60, 150]);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const newTotal = (data.currentCount || 0) + (parseFloat(qty) || 0);
  const diff = newTotal - (data.mevcut || 0);

  const bump = (n) => setQty((q) => Math.max(0, (parseFloat(q) || 0) + n * (isKg ? 0.1 : 1)));
  const commit = () => {
    const n = parseFloat(qty) || 0;
    if (n <= 0) {
      onClose();
      return;
    }
    // ✅ Onay bipi + kısa titreşim
    beep(1800, 100, 0.3);
    vib(50);
    onAdd(n);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md"
        onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
        data-testid="quickadd-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#D9653B]" />
            Bu üründen ne kadar daha?
            {isKg && (
              <span className="text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5 font-heading font-bold">KG</span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#1B2A22]/70">
            {isKg
              ? "Bu ürün KG ile satılıyor. Terazi değerini (kg) yaz, listedeki miktara eklenir."
              : "Aynı ürünü tekrar okuttun. Yeni gelen miktarı buraya yaz, listedeki sayıma eklenir."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Ürün bilgisi */}
          <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
            <p className="font-heading font-bold text-base text-[#1B2A22] leading-tight" data-testid="quickadd-name">
              {data.name}
            </p>
            <p className="text-[11px] font-mono text-[#1B2A22]/60 mt-0.5">
              {data.akinsoft_code || "—"} · {data.barcode || "—"}
            </p>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div className="bg-[#1B2A22]/5 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-[#1B2A22]/60">Mevcut</div>
                <div className="font-mono font-bold text-sm" data-testid="quickadd-mevcut">{data.mevcut} {unitLabel}</div>
              </div>
              <div className="bg-[#2E5A36]/10 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-[#2E5A36]">Şu an sayılan</div>
                <div className="font-mono font-bold text-sm text-[#2E5A36]" data-testid="quickadd-current">{data.currentCount} {unitLabel}</div>
              </div>
              <div className="bg-[#D9653B]/10 rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wider text-[#D9653B]">Yeni toplam</div>
                <div className="font-mono font-extrabold text-base text-[#D9653B]" data-testid="quickadd-newtotal">{newTotal} {unitLabel}</div>
              </div>
            </div>
          </div>

          {/* Miktar girişi — büyük, dokunmatik dostu */}
          <div className="bg-white border border-[#E8E2D5] rounded-xl p-3">
            <div className="text-[11px] font-heading font-bold text-[#1B2A22] mb-2">
              + Eklenecek miktar {isKg ? "(kg)" : "(adet)"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => bump(-1)}
                className="h-14 w-14 rounded-full"
                data-testid="quickadd-minus"
              >
                <Minus className="w-5 h-5" />
              </Button>
              <Input
                ref={inputRef}
                type="number"
                inputMode="decimal"
                step={isKg ? "0.01" : "any"}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="flex-1 text-center h-14 text-2xl font-mono font-extrabold bg-[#FAF7F2]"
                data-testid="quickadd-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => bump(1)}
                className="h-14 w-14 rounded-full"
                data-testid="quickadd-plus"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
            {/* Hızlı seçim — KG için ondalık, adet için tam sayı */}
            <div className="grid grid-cols-6 gap-1.5 mt-2">
              {quickPicks.map((n) => (
                <Button
                  key={n}
                  type="button"
                  variant="outline"
                  onClick={() => setQty(String(n))}
                  className="h-9 rounded-full text-xs font-mono font-bold"
                  data-testid={`quickadd-quick-${n}`}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>

          {/* Fark önizleme */}
          <div className={`text-center text-xs font-mono font-bold ${diff === 0 ? "text-gray-500" : diff > 0 ? "text-green-600" : "text-red-600"}`}>
            Mevcut ile fark: {diff > 0 ? "+" : ""}{diff.toFixed(2)} {unitLabel}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full h-12"
              data-testid="quickadd-cancel"
            >
              <X className="w-4 h-4 mr-1" /> İptal
            </Button>
            <Button
              onClick={commit}
              disabled={!(parseFloat(qty) > 0)}
              className="flex-[2] rounded-full h-12 bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold text-base"
              data-testid="quickadd-confirm"
            >
              <Check className="w-5 h-5 mr-2" />
              +{parseFloat(qty) || 0} {unitLabel} Ekle (toplam {newTotal} {unitLabel})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
